import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
    ResourceEmployee,
    ResourceHiddenExternalEvent
} from '../../../../Models/resource.models';
import { ResourceApiService } from '../../../../Services/resource-api.service';

export interface ResourceHiddenExternalEventsDialogData {
    /** Mitarbeiter der Planung – zur Anzeige der Namen (die Liste enthält nur Benutzer-IDs). */
    employees: ResourceEmployee[];
    /** true = „Mein Kalender“: nur eigene ausgeblendete Termine (eigene Endpunkte). */
    personal?: boolean;
}

export interface ResourceHiddenExternalEventsDialogResult {
    /** true, wenn mindestens ein Termin wieder eingeblendet wurde (Planung neu laden). */
    changed: boolean;
    /** Anzahl der nach dem Schließen noch ausgeblendeten Termine. */
    remainingCount: number;
}

/**
 * Liste der ausgeblendeten Outlook-Termine der Ressourcenplanung mit der
 * Möglichkeit, einzelne oder alle Termine wieder einzublenden.
 */
@Component({
    selector: 'app-resource-hidden-external-events-dialog',
    templateUrl: './resource-hidden-external-events-dialog.component.html',
    styleUrls: ['./resource-hidden-external-events-dialog.component.css']
})
export class ResourceHiddenExternalEventsDialogComponent implements OnInit {

    events: ResourceHiddenExternalEvent[] = [];
    loading = true;
    loadError = '';

    /** ID des Termins, der gerade wieder eingeblendet wird. */
    busyId: number | null = null;
    busyAll = false;

    private changed = false;

    constructor(
        private api: ResourceApiService,
        private snackBar: MatSnackBar,
        private dialogRef: MatDialogRef<
            ResourceHiddenExternalEventsDialogComponent,
            ResourceHiddenExternalEventsDialogResult
        >,
        @Inject(MAT_DIALOG_DATA) public data: ResourceHiddenExternalEventsDialogData
    ) {
        // disableClose ist gesetzt, damit das Ergebnis (changed) auch beim
        // Klick auf den Hintergrund oder bei Escape beim Aufrufer ankommt.
        this.dialogRef.backdropClick().subscribe(() => this.close());
        this.dialogRef.keydownEvents().subscribe(event => {
            if (event.key === 'Escape') {
                this.close();
            }
        });
    }

    ngOnInit(): void {
        this.load();
    }

    load(): void {
        this.loading = true;
        this.loadError = '';

        (this.data.personal ? this.api.getMyHiddenExternalEvents() : this.api.getHiddenExternalEvents()).subscribe({
            next: events => {
                this.events = events;
                this.loading = false;
            },
            error: err => {
                this.loading = false;
                this.loadError = 'Ausgeblendete Termine konnten nicht geladen werden: ' + this.errorText(err);
            }
        });
    }

    get busy(): boolean {
        return this.busyAll || this.busyId !== null;
    }

    trackEvent(_index: number, event: ResourceHiddenExternalEvent): number {
        return event.id;
    }

    employeeName(userId: string): string {
        return this.data.employees.find(employee => employee.id === userId)?.name
            ?? 'Unbekannter Mitarbeiter';
    }

    /**
     * Zeitraum als Text. Die Zeitstempel sind Wandzeiten mit "Z"-Endung (wie bei
     * den Outlook-Terminen der Planung) und werden deshalb nicht über die
     * Date-Pipe (Zeitzonenverschiebung) formatiert, sondern direkt gelesen.
     */
    formatPeriod(event: ResourceHiddenExternalEvent): string {
        const startDate = event.start.substring(0, 10);
        let endDate = event.end.substring(0, 10);

        if (event.allDay) {
            // Bei ganztägigen iCalendar-Terminen ist das Enddatum exklusiv.
            endDate = this.shiftDate(endDate, -1);
            if (endDate < startDate) {
                endDate = startDate;
            }
            return startDate === endDate
                ? `${this.formatDate(startDate)} · ganztägig`
                : `${this.formatDate(startDate)} – ${this.formatDate(endDate)} · ganztägig`;
        }

        const startTime = event.start.substring(11, 16);
        const endTime = event.end.substring(11, 16);

        if (startDate === endDate) {
            return `${this.formatDate(startDate)}, ${startTime} – ${endTime} Uhr`;
        }

        return `${this.formatDate(startDate)} ${startTime} – ${this.formatDate(endDate)} ${endTime} Uhr`;
    }

    restore(event: ResourceHiddenExternalEvent): void {
        if (this.busy) {
            return;
        }

        this.busyId = event.id;

        (this.data.personal ? this.api.unhideMyExternalEvent(event.id) : this.api.unhideExternalEvent(event.id)).subscribe({
            next: () => {
                this.busyId = null;
                this.removeFromList(event.id);
                this.changed = true;
                this.snackBar.open('Termin wieder eingeblendet.', undefined, { duration: 2500 });
            },
            error: err => {
                this.busyId = null;

                // 404: Bereits von jemand anderem eingeblendet – aus der Liste nehmen.
                if (err?.status === 404) {
                    this.removeFromList(event.id);
                    this.changed = true;
                    return;
                }

                this.snackBar.open(
                    'Einblenden fehlgeschlagen: ' + this.errorText(err),
                    'OK',
                    { duration: 6000 }
                );
            }
        });
    }

    restoreAll(): void {
        if (this.busy || this.events.length === 0) {
            return;
        }

        this.busyAll = true;

        (this.data.personal ? this.api.unhideAllMyExternalEvents() : this.api.unhideAllExternalEvents()).subscribe({
            next: count => {
                this.busyAll = false;
                this.events = [];
                this.changed = true;
                this.snackBar.open(
                    count === 1
                        ? '1 Termin wieder eingeblendet.'
                        : `${count} Termine wieder eingeblendet.`,
                    undefined,
                    { duration: 2500 }
                );
            },
            error: err => {
                this.busyAll = false;
                this.snackBar.open(
                    'Einblenden fehlgeschlagen: ' + this.errorText(err),
                    'OK',
                    { duration: 6000 }
                );
                // Der tatsächliche Stand ist unklar – Liste neu laden.
                this.load();
            }
        });
    }

    close(): void {
        if (this.busy) {
            return;
        }

        this.dialogRef.close({
            changed: this.changed,
            remainingCount: this.events.length
        });
    }

    private removeFromList(id: number): void {
        this.events = this.events.filter(event => event.id !== id);
    }

    private formatDate(isoDate: string): string {
        const [year, month, day] = isoDate.split('-');
        return `${day}.${month}.${year}`;
    }

    private shiftDate(isoDate: string, days: number): string {
        const [year, month, day] = isoDate.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day + days));
        return date.toISOString().substring(0, 10);
    }

    private errorText(err: any): string {
        const body = err?.error;

        if (typeof body === 'string' && body.trim()) {
            return body;
        }
        if (body && typeof body === 'object' && typeof body.title === 'string') {
            return body.title;
        }
        if (err?.status === 403) {
            return 'Keine Berechtigung.';
        }
        if (err?.status === 0) {
            return 'Server nicht erreichbar.';
        }

        return err?.message ?? 'Unbekannter Fehler.';
    }
}
